// Groups (plans/SLIDES_ARTEFACT.md "Groups", AB#175, from spike B) and the selection rules the
// mockup validated. The pure helpers read a SlideSnapshot; each mutation runs as ONE Y transaction
// (so a concurrent edit is never split between "before" and "after" a group/ungroup) and writes the
// same structure as the backend's codec (adapters/room/slides-crdt.ts), pinned by
// shared/slides-model.fixture.json. A mutation the selection does not allow returns null / [] (the
// editor simply does nothing) where the backend refuses with a SlideOpError.
//
// Coordinates: every element, grouped or not, stores SLIDE coordinates. A group's box is the union
// of its members' boxes (unrotated frame); the stored box is a cache kept fresh by every mutation
// here, while readers derive it. Rotating a group sets `rot` on the group only.
import * as Y from 'yjs'
import {
  LOCAL_ORIGIN,
  childrenOf,
  elementsOf,
  migratePadding,
  newId,
  orderOf,
  round,
  slideMap,
  snapshotSlide,
  unionBox,
  type Box,
  type ElementSnapshot,
  type RegroupMemory,
  type SlideSnapshot,
} from './model'
import { freezeConnectors } from './connector-edits'

// ── pure: structure ─────────────────────────────────────────────────────────────────────────────

/** id → the group that holds it, or null for a top-level element. */
export function parentsOf(s: SlideSnapshot): Map<string, string | null> {
  const parents = new Map<string, string | null>()
  for (const id of Object.keys(s.elements)) parents.set(id, null)
  for (const el of Object.values(s.elements)) {
    for (const child of el.children ?? []) if (child in s.elements) parents.set(child, el.id)
  }
  return parents
}

/** The chain from the top-level ancestor down to `id` (inclusive). */
export function pathTo(s: SlideSnapshot, id: string): string[] {
  const parents = parentsOf(s)
  const path: string[] = []
  let cur: string | null | undefined = id
  // The visited guard: two concurrent nestings can, in theory, make a cycle.
  while (cur && !path.includes(cur)) {
    path.unshift(cur)
    cur = parents.get(cur)
  }
  return path
}

/** Every element nested under `id`, at any depth (not `id` itself). */
export function descendantsOf(s: SlideSnapshot, id: string): string[] {
  const out: string[] = []
  const walk = (gid: string): void => {
    for (const c of s.elements[gid]?.children ?? []) {
      if (!(c in s.elements) || c === id || out.includes(c)) continue
      out.push(c)
      walk(c)
    }
  }
  walk(id)
  return out
}

/** Rotate a point around a centre, degrees clockwise (CSS / PowerPoint convention). */
export function rotatePoint(px: number, py: number, cx: number, cy: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180
  const dx = px - cx
  const dy = py - cy
  return [cx + dx * Math.cos(a) - dy * Math.sin(a), cy + dx * Math.sin(a) + dy * Math.cos(a)]
}

// ── pure: selection (mockup behaviour) ──────────────────────────────────────────────────────────

/**
 * Pointer down on `leafId` (the deepest element under the pointer).
 * - Inside the current selection (on it or on one of its members): keep it, so a drag moves it.
 * - Inside the group the selection lives in: select the sibling under the pointer (you stay "in"
 *   the group, as in PowerPoint).
 * - Elsewhere: the OUTERMOST group (or the element itself when it is top-level).
 * `additive` (Ctrl/Cmd+click) toggles that candidate when it shares the selection's parent.
 */
export function selectOnPointerDown(
  s: SlideSnapshot,
  leafId: string,
  selection: string[],
  additive = false,
): string[] {
  const path = pathTo(s, leafId)
  const parents = parentsOf(s)
  const hit = selection.find((id) => path.includes(id))
  if (hit && !additive) return selection
  const context = selection.length > 0 ? parents.get(selection[0] as string) ?? null : null
  const candidate =
    context !== null && path.includes(context) ? (path[path.indexOf(context) + 1] as string) : (path[0] as string)
  if (additive) {
    if (selection.includes(candidate)) return selection.filter((id) => id !== candidate)
    if (selection.length === 0 || parents.get(candidate) === context) return [...selection, candidate]
  }
  return [candidate]
}

/** A click (pointer up without a drag) on an already-selected group goes one level in. */
export function selectOnClick(s: SlideSnapshot, leafId: string, selection: string[]): string[] {
  if (selection.length !== 1) return selection
  const sel = selection[0] as string
  const path = pathTo(s, leafId)
  const at = path.indexOf(sel)
  if (at < 0 || at === path.length - 1 || s.elements[sel]?.kind !== 'group') return selection
  return [path[at + 1] as string]
}

/** Esc: up one level (the selection's group), or nothing selected at the top. */
export function selectParent(s: SlideSnapshot, selection: string[]): string[] {
  if (selection.length === 0) return []
  const parent = parentsOf(s).get(selection[0] as string)
  return parent ? [parent] : []
}

// ── mutations ───────────────────────────────────────────────────────────────────────────────────

/** The Y.Array an element sits in: the slide's `order`, or its group's `children`. */
function listOf(slide: Y.Map<unknown>, parent: string | null): Y.Array<string> {
  if (parent === null) return orderOf(slide)
  return childrenOf(elementsOf(slide).get(parent) as Y.Map<unknown>) as Y.Array<string>
}

function moveBy(el: Y.Map<unknown> | undefined, dx: number, dy: number): void {
  if (!el) return
  el.set('x', round(Number(el.get('x') ?? 0) + dx))
  el.set('y', round(Number(el.get('y') ?? 0) + dy))
}

function writeBox(el: Y.Map<unknown> | undefined, box: Box): void {
  if (!el) return
  for (const k of ['x', 'y', 'w', 'h'] as const) {
    const v = round(box[k])
    if (el.get(k) !== v) el.set(k, v)
  }
}

/**
 * Re-fit the stored box of every group above `id` to the union of its members (PowerPoint does
 * this when a member moves or resizes inside a group). Readers derive the box anyway; keeping the
 * cache fresh means the backend, which bakes a rotated group's rotation around its stored centre on
 * ungroup, sees what members see.
 */
function refitAncestors(doc: Y.Doc, slideId: string, id: string): void {
  const els = elementsOf(slideMap(doc, slideId))
  let s = snapshotSlide(doc, slideId)
  let parent = parentsOf(s).get(id) ?? null
  while (parent) {
    writeBox(els.get(parent), s.elements[parent] as ElementSnapshot)
    s = snapshotSlide(doc, slideId)
    parent = parentsOf(s).get(parent) ?? null
  }
}

/**
 * Group (Ctrl+G): 2+ elements sharing one parent become a group whose box is their union. They
 * leave their list (keeping their relative z-order) and the group takes the frontmost one's place.
 * Grouping a member into something new breaks its old "Regroup". Returns the group id, or null
 * when the selection cannot be grouped.
 */
export function groupElements(
  doc: Y.Doc,
  slideId: string,
  ids: string[],
  groupId = newId(doc, 'g'),
  origin: unknown = LOCAL_ORIGIN,
): string | null {
  const s = snapshotSlide(doc, slideId)
  const parents = parentsOf(s)
  const unique = [...new Set(ids)].filter((id) => id in s.elements)
  if (unique.length < 2 || unique.length < new Set(ids).size || groupId in s.elements) return null
  const parent = parents.get(unique[0] as string) ?? null
  if (unique.some((id) => (parents.get(id) ?? null) !== parent)) return null
  const slide = slideMap(doc, slideId)
  const els = elementsOf(slide)
  doc.transact(() => {
    const list = listOf(slide, parent)
    const current = list.toArray()
    const members = current.filter((id) => unique.includes(id))
    const front = Math.max(...members.map((id) => current.indexOf(id)))
    const g = new Y.Map<unknown>()
    g.set('kind', 'group')
    g.set('rot', 0)
    g.set('style', new Y.Map())
    g.set('children', Y.Array.from(members))
    els.set(groupId, g)
    writeBox(g, unionBox(members.map((id) => s.elements[id] as ElementSnapshot)))
    for (let i = current.length - 1; i >= 0; i--) if (members.includes(current[i] as string)) list.delete(i, 1)
    list.insert(front - (members.length - 1), [groupId])
    for (const id of members) els.get(id)?.delete('regroup')
    refitAncestors(doc, slideId, groupId)
  }, origin)
  return groupId
}

/**
 * Ungroup (Ctrl+Shift+G): members return to the parent list at the group's place, keep their
 * positions, and remember the group (`regroup`). A rotated group's rotation is baked into its
 * members (each member turns around the group centre), so nothing moves on screen.
 */
export function ungroup(doc: Y.Doc, slideId: string, groupId: string, origin: unknown = LOCAL_ORIGIN): string[] {
  const s = snapshotSlide(doc, slideId)
  const g = s.elements[groupId]
  if (!g || g.kind !== 'group') return []
  const parent = parentsOf(s).get(groupId) ?? null
  const members = g.children ?? []
  const slide = slideMap(doc, slideId)
  const els = elementsOf(slide)
  doc.transact(() => {
    if (g.rot !== 0) {
      const cx = g.x + g.w / 2
      const cy = g.y + g.h / 2
      for (const id of members) {
        const m = s.elements[id] as ElementSnapshot
        const [nx, ny] = rotatePoint(m.x + m.w / 2, m.y + m.h / 2, cx, cy, g.rot)
        const dx = nx - (m.x + m.w / 2)
        const dy = ny - (m.y + m.h / 2)
        for (const d of [id, ...descendantsOf(s, id)]) moveBy(els.get(d), dx, dy)
        els.get(id)?.set('rot', (((m.rot + g.rot) % 360) + 360) % 360)
      }
    }
    const list = listOf(slide, parent)
    const at = list.toArray().indexOf(groupId)
    list.delete(at, 1)
    list.insert(at, members)
    for (const id of members) els.get(id)?.set('regroup', { groupId, members } satisfies RegroupMemory)
    els.delete(groupId)
    for (const id of members) refitAncestors(doc, slideId, id)
  }, origin)
  return members
}

/**
 * Regroup: from any one ungrouped member, rebuild the same group (same id) out of the members that
 * still exist, still remember that group, and sit in the same container.
 */
export function regroup(doc: Y.Doc, slideId: string, memberId: string, origin: unknown = LOCAL_ORIGIN): string | null {
  const s = snapshotSlide(doc, slideId)
  const info = s.elements[memberId]?.regroup
  if (!info || info.groupId in s.elements) return null
  const parents = parentsOf(s)
  const parent = parents.get(memberId) ?? null
  const back = info.members.filter(
    (id) => s.elements[id]?.regroup?.groupId === info.groupId && (parents.get(id) ?? null) === parent,
  )
  return groupElements(doc, slideId, back, info.groupId, origin)
}

/** Move elements (and everything nested in them) by a delta; parent groups re-fit. */
export function translate(
  doc: Y.Doc,
  slideId: string,
  moves: Array<{ id: string; dx: number; dy: number }>,
  origin: unknown = LOCAL_ORIGIN,
): void {
  const s = snapshotSlide(doc, slideId)
  const els = elementsOf(slideMap(doc, slideId))
  doc.transact(() => {
    for (const { id, dx, dy } of moves) {
      if (dx === 0 && dy === 0) continue
      for (const d of [id, ...descendantsOf(s, id)]) moveBy(els.get(d), dx, dy)
    }
    for (const { id } of moves) refitAncestors(doc, slideId, id)
  }, origin)
}

/**
 * Resize an element to `box`. For a group, every member is scaled into the new box (positions and
 * sizes; NOT font sizes) — this is the "bake at drag end" step; the drag itself only previews.
 * A legacy px padding is turned into % of the box it had first (model.ts#migratePadding).
 */
export function resizeTo(doc: Y.Doc, slideId: string, id: string, box: Box, origin: unknown = LOCAL_ORIGIN): void {
  const s = snapshotSlide(doc, slideId)
  const el = s.elements[id]
  if (!el) return
  const els = elementsOf(slideMap(doc, slideId))
  doc.transact(() => {
    if (el.kind === 'group' && el.w > 0 && el.h > 0) {
      const sx = box.w / el.w
      const sy = box.h / el.h
      for (const d of descendantsOf(s, id)) {
        const m = s.elements[d] as ElementSnapshot
        migratePadding(els.get(d))
        writeBox(els.get(d), {
          x: box.x + (m.x - el.x) * sx,
          y: box.y + (m.y - el.y) * sy,
          w: m.w * sx,
          h: m.h * sy,
        })
      }
    }
    // A legacy px padding scales from here on, as a % one does (AB#311).
    migratePadding(els.get(id))
    writeBox(els.get(id), box)
    refitAncestors(doc, slideId, id)
  }, origin)
}

/**
 * Delete elements (and their members). A group left with one member dissolves into it, in the
 * same transaction (the server's repair would do it anyway; doing it here keeps it one undo step).
 * A connector glued to what goes keeps that end frozen where it was (connector-edits.ts#freezeConnectors).
 */
export function removeElements(doc: Y.Doc, slideId: string, ids: string[], origin: unknown = LOCAL_ORIGIN): void {
  const slide = slideMap(doc, slideId)
  const els = elementsOf(slide)
  doc.transact(() => {
    const before = snapshotSlide(doc, slideId)
    const gone = new Set(ids.filter((id) => id in before.elements).flatMap((id) => [id, ...descendantsOf(before, id)]))
    freezeConnectors(doc, slideId, gone, origin)
    for (const id of ids) {
      const s = snapshotSlide(doc, slideId)
      if (!(id in s.elements)) continue
      const parents = parentsOf(s)
      const parent = parents.get(id) ?? null
      const list = listOf(slide, parent)
      const at = list.toArray().indexOf(id)
      if (at >= 0) list.delete(at, 1)
      for (const d of [id, ...descendantsOf(s, id)]) els.delete(d)
      if (parent && list.length <= 1) {
        const glist = listOf(slide, parents.get(parent) ?? null)
        const gat = glist.toArray().indexOf(parent)
        const rest = list.toArray()
        if (gat >= 0) {
          glist.delete(gat, 1)
          if (rest.length) glist.insert(gat, rest)
        }
        els.delete(parent)
        for (const r of rest) refitAncestors(doc, slideId, r)
      } else if (parent) {
        refitAncestors(doc, slideId, list.get(0) as string)
      }
    }
  }, origin)
}
